import { useState, useEffect } from 'react';
import axios from 'axios';
import ReportForm from './components/ReportForm';
import ReportList from './components/ReportList';
import ReportViewer from './components/ReportViewer';
import GenerationProgress from './components/GenerationProgress';
import SourcesModal from './components/SourcesModal';

const API_BASE = '/api';

function App() {
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('list');
  const [generatingReportId, setGeneratingReportId] = useState(null);
  const [sourcesModalOpen, setSourcesModalOpen] = useState(false);
  const [selectedSources, setSelectedSources] = useState(null);

  useEffect(() => {
    fetchReports();
  }, []);

  async function fetchReports() {
    try {
      const response = await axios.get(`${API_BASE}/reports`);
      setReports(response.data.reports || []);
    } catch (error) {
      console.error('Failed to fetch reports:', error);
    } finally {
      setLoading(false);
    }
  }

  function handleReportGenerated(report) {
    setGeneratingReportId(null);
    fetchReports();
    setActiveTab('list');
  }

  function handleViewReport(report) {
    setSelectedReport(report);
    setActiveTab('view');
  }

  async function handleViewSources(report) {
    try {
      const response = await axios.get(`${API_BASE}/reports/${report.id}/sources`);
      setSelectedSources(response.data.sources);
      setSourcesModalOpen(true);
    } catch (error) {
      console.error('Failed to fetch sources:', error);
      alert('Failed to load news sources. Please try again.');
    }
  }

  async function handleDeleteReport(id) {
    try {
      await axios.delete(`${API_BASE}/reports/${id}`);
      setReports(reports.filter(r => r.id !== id));
      if (selectedReport?.id === id) {
        setSelectedReport(null);
        setActiveTab('list');
      }
    } catch (error) {
      console.error('Failed to delete report:', error);
      alert('Failed to delete report. Please try again.');
    }
  }

  function handleBackToList() {
    setSelectedReport(null);
    setActiveTab('list');
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <header className="mb-12">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-semibold text-slate-900 mb-2">
                Market Intelligence Platform
              </h1>
              <p className="text-slate-600 text-sm">
                AI-powered industry analysis and strategic insights
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="h-2 w-2 bg-emerald-500 rounded-full animate-pulse"></div>
              <span className="text-xs text-slate-500 font-medium">System Active</span>
            </div>
          </div>
          <div className="h-px bg-gradient-to-r from-slate-200 via-slate-100 to-transparent"></div>
        </header>

        {/* Navigation */}
        <nav className="mb-8">
          <div className="flex gap-1 bg-white rounded-lg p-1 shadow-sm border border-slate-200 inline-flex">
            <button
              onClick={() => setActiveTab('create')}
              className={`px-5 py-2.5 rounded-md text-sm font-medium transition-all ${
                activeTab === 'create'
                  ? 'bg-slate-900 text-white shadow-sm'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              Generate Report
            </button>
            <button
              onClick={() => setActiveTab('list')}
              className={`px-5 py-2.5 rounded-md text-sm font-medium transition-all ${
                activeTab === 'list'
                  ? 'bg-slate-900 text-white shadow-sm'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              Reports Library
              {reports.length > 0 && (
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  activeTab === 'list' ? 'bg-white/20' : 'bg-slate-100'
                }`}>
                  {reports.length}
                </span>
              )}
            </button>
          </div>
        </nav>

        {/* Content */}
        <main className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <GenerationProgress 
            isGenerating={generatingReportId !== null} 
            reportId={generatingReportId} 
          />
          {activeTab === 'create' && (
            <ReportForm 
              onReportGenerated={handleReportGenerated} 
              onGeneratingStart={(reportId) => setGeneratingReportId(reportId)}
            />
          )}
          
          {activeTab === 'list' && (
            <ReportList
              reports={reports}
              loading={loading}
              onViewReport={handleViewReport}
              onDeleteReport={handleDeleteReport}
              onViewSources={handleViewSources}
            />
          )}
          
          {activeTab === 'view' && selectedReport && (
            <ReportViewer
              report={selectedReport}
              onBack={handleBackToList}
            />
          )}
        </main>

        {/* Sources Modal */}
        {sourcesModalOpen && selectedSources && (
          <SourcesModal
            sources={selectedSources}
            onClose={() => {
              setSourcesModalOpen(false);
              setSelectedSources(null);
            }}
          />
        )}

        {/* Footer */}
        <footer className="mt-12 text-center">
          <div className="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent mb-6"></div>
          <p className="text-xs text-slate-500">
            Market Intelligence Platform © {new Date().getFullYear()}
          </p>
          <p className="text-xs text-slate-400 mt-1">
            Confidential business intelligence for authorized users
          </p>
        </footer>
      </div>
    </div>
  );
}

export default App;
